// File: app/src/utils/formatLabel.js
//
// Display-time formatter for workout-node labels. The canonical exercise key
// used by the rep estimator is *derived* from the raw label at lookup time
// (see `normaliseExerciseKey` in repEstimator.js — it lowercases, strips
// punctuation, drops equipment qualifiers, and resolves through a SYNONYMS
// table). That means we can freely Title-Case for display without breaking
// any downstream math. A single `label` field is both display source and
// lookup input.
//
// Rules:
//   • snake_case → spaces (so `ball_slam` becomes `ball slam`).
//   • Title-Case each word.
//   • Preserve common all-caps tokens (`AMRAP`, `EMOM`, `TGU`, `HIIT`,
//     `RPE`, `RIR`, `1RM`, `OHP`).
//   • Preserve hyphens (`push-up` → `Push-Up`).
//   • If the raw label already contains a space and isn't all-lowercase /
//     all-snake_case, leave it alone (it's almost certainly already
//     human-readable — `"Concept 2 Rower"`, `"Dumbbell Push-up"`).
//   • Pass through `null`/`undefined`/empty strings unchanged.
//
// Used by SessionPlanDisplay, SessionPlanOutline, WorkoutCoachView. The
// underlying `node.label` is never mutated.

const PRESERVED_TOKENS = new Set([
  "AMRAP",
  "EMOM",
  "TGU",
  "HIIT",
  "RPE",
  "RIR",
  "OHP",
  "1RM",
]);

// Words that should remain lowercase in title case (English conventions).
// Kept small — we Title-Case everything by default and only suppress these
// when they appear inside a multi-word label.
const LOWERCASE_SHORT_WORDS = new Set(["of", "to", "and", "the", "a", "an"]);

const titleCaseWord = (word, isFirst) => {
  if (!word) return word;
  const upper = word.toUpperCase();
  if (PRESERVED_TOKENS.has(upper)) return upper;
  const lower = word.toLowerCase();
  if (!isFirst && LOWERCASE_SHORT_WORDS.has(lower)) return lower;
  // Handle hyphen-joined tokens (e.g. "push-up" → "Push-Up").
  if (lower.includes("-")) {
    return lower
      .split("-")
      .map((part, i) => titleCaseWord(part, i === 0 && isFirst))
      .join("-");
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const isLowerSnake = (s) => /^[a-z0-9_]+$/.test(s);

/**
 * Format a workout-node label for display.
 *
 * @param {string|null|undefined} label  Raw label from the plan tree.
 * @returns {string} Display-formatted label (or original if no change needed).
 */
export const formatLabel = (label) => {
  if (label == null) return label;
  const raw = String(label);
  if (!raw.trim()) return raw;
  // Already human-readable (contains a space and is not pure snake_case /
  // pure lowercase) — leave it alone. This keeps user-authored labels
  // ("Concept 2 Rower", "Dumbbell Push-up") untouched.
  if (raw.includes(" ") && raw !== raw.toLowerCase()) return raw;
  // Replace _ with space and collapse whitespace, then title-case word-by-word.
  const spaced = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  if (!spaced) return raw;
  // Skip work if the source was purely lowercase snake_case but contained no
  // alphanumerics after normalisation.
  const words = spaced.split(" ");
  const formatted = words.map((w, i) => titleCaseWord(w, i === 0)).join(" ");
  // Special-case: if the original was a one-word lowercase snake_case token
  // and the formatted form differs by capitalisation only, return formatted.
  if (isLowerSnake(raw) || raw === raw.toLowerCase()) return formatted;
  return formatted;
};

/**
 * Convenience wrapper for callers that prefer a `node` argument shape.
 */
export const formatNodeLabel = (node) =>
  formatLabel(node?.label || node?.exercise_name);

export default formatLabel;
